import { PLOT_MAP_HEADERS } from "@/services/firebase";
import type {
  createExperimentWithPlots,
  createPlotsFromGrid,
  createPlotsFromSequence,
  deleteExperiment,
  PlotCreationProgressCallback,
} from "@/services/firebase";
import type { CreateExperimentInput, Experiment } from "@/types/experiment";
import {
  calculateColsFromData,
  calculateRowsFromData,
  dropColumnsFromHeaders,
} from "@/utils/csvUtils";
import type { PlotMapChangeData, PlotMapData } from "./PlotMapSection";

export interface ExperimentBasics {
  name: string;
  description: string;
  location: string;
}

/**
 * Build the experiment payload for creation from the plot-map change data.
 *
 * For uploaded plot maps, `droppedColumns` are removed from the experiment's
 * stored column schema (`plotMap.headers` and `metadata.originalCsvHeaders`) —
 * this is the entire persistence effect of "dropping" an empty metadata column.
 * Per-plot metadata already omits blank values, so nothing else needs filtering.
 */
export function buildExperimentInput(
  basics: ExperimentBasics,
  plotMapChangeData: PlotMapChangeData | null,
  droppedColumns: string[] = []
): CreateExperimentInput {
  const name = basics.name || "New Experiment";
  const description = basics.description || "";
  const location = basics.location || "";

  const csvValidationResult = plotMapChangeData?.csvValidationResult;
  if (csvValidationResult) {
    const plotIdColumn = plotMapChangeData?.plotIdColumn || "";
    const rowColumn = plotMapChangeData?.rowColumn || "";
    const colColumn = plotMapChangeData?.colColumn || "";
    const keptHeaders = dropColumnsFromHeaders(csvValidationResult.headers || [], droppedColumns);

    return {
      name,
      description,
      location,
      plotMap: {
        headers: keptHeaders,
        plotIDName: plotIdColumn,
        rowIDName: rowColumn || undefined,
        colIDName: colColumn || undefined,
        numRows: calculateRowsFromData(csvValidationResult.parsedData || [], rowColumn),
        numCols: calculateColsFromData(csvValidationResult.parsedData || [], colColumn),
      },
      metadata: {
        csvFileName: plotMapChangeData?.file?.name,
        originalCsvHeaders: keptHeaders,
        csvRowCount: csvValidationResult.parsedData?.length || 0,
      },
    };
  }

  if (plotMapChangeData?.type === "generate" && plotMapChangeData.rows && plotMapChangeData.columns) {
    return {
      name,
      description,
      location,
      plotMap: {
        headers: [PLOT_MAP_HEADERS.PLOT_ID, PLOT_MAP_HEADERS.ROW, PLOT_MAP_HEADERS.COLUMN],
        plotIDName: PLOT_MAP_HEADERS.PLOT_ID,
        rowIDName: PLOT_MAP_HEADERS.ROW,
        colIDName: PLOT_MAP_HEADERS.COLUMN,
        numRows: plotMapChangeData.rows,
        numCols: plotMapChangeData.columns,
      },
      metadata: {},
    };
  }

  if (plotMapChangeData?.type === "sequence") {
    return {
      name,
      description,
      location,
      plotMap: {
        headers: [PLOT_MAP_HEADERS.PLOT_ID],
        plotIDName: PLOT_MAP_HEADERS.PLOT_ID,
      },
      metadata: {},
    };
  }

  return { name, description, location, metadata: {} };
}

/** Build the plot-map payload (file / grid / sequence) passed alongside the experiment. */
export function buildPlotMapData(
  plotMapChangeData: PlotMapChangeData | null
): PlotMapData | undefined {
  if (!plotMapChangeData) return undefined;

  if (plotMapChangeData.type === "upload" && plotMapChangeData.file) {
    return {
      type: "upload",
      file: plotMapChangeData.file,
      ...(plotMapChangeData.originalFile && plotMapChangeData.selectedSheet && {
        originalFile: plotMapChangeData.originalFile,
        selectedSheet: plotMapChangeData.selectedSheet,
      }),
    };
  }

  if (plotMapChangeData.type === "generate" && plotMapChangeData.rows && plotMapChangeData.columns) {
    return {
      type: "generate",
      rows: plotMapChangeData.rows,
      columns: plotMapChangeData.columns,
    };
  }

  if (plotMapChangeData.type === "sequence" && plotMapChangeData.count) {
    return {
      type: "sequence",
      count: plotMapChangeData.count,
      ...(plotMapChangeData.customNames && { customNames: plotMapChangeData.customNames }),
    };
  }

  return undefined;
}

/** The services `submitNewExperiment` drives, injected so the flow is testable. */
export interface NewExperimentServices {
  createExperimentWithPlots: typeof createExperimentWithPlots;
  createPlotsFromGrid: typeof createPlotsFromGrid;
  createPlotsFromSequence: typeof createPlotsFromSequence;
  deleteExperiment: typeof deleteExperiment;
}

/**
 * Create an experiment and its plot map (uploaded file, generated grid, or
 * sequence). Every write — including the rollback on plot failure — targets
 * `orgId`, captured once by the caller, so a mid-flight organization switch
 * cannot split the experiment and its plots across organizations.
 */
export async function submitNewExperiment(
  services: NewExperimentServices,
  args: {
    orgId: string;
    trialId: string;
    experimentData: CreateExperimentInput;
    plotMapData?: PlotMapData;
    onProgress?: PlotCreationProgressCallback;
  }
): Promise<Experiment | null> {
  const { orgId, trialId, experimentData, plotMapData, onProgress } = args;
  const scope = { orgId };
  let createdExperiment: Experiment | null = null;

  if (plotMapData?.type === "upload") {
    // Create experiment with CSV/Excel plot map
    createdExperiment = await services.createExperimentWithPlots(
      trialId,
      experimentData,
      plotMapData.file,
      onProgress,
      scope
    );
  } else if (plotMapData?.type === "generate" || plotMapData?.type === "sequence") {
    // Create experiment first, then generate plots
    onProgress?.(0.1, "Creating experiment...");

    // Use createExperimentWithPlots without a file
    createdExperiment = await services.createExperimentWithPlots(trialId, experimentData, undefined, undefined, scope);

    if (createdExperiment?.id) {
      const onPlotProgress: PlotCreationProgressCallback = (progress, message) => {
        // Scale progress from 0.3 to 1.0
        onProgress?.(0.3 + progress * 0.7, message);
      };

      let plotResult: Awaited<ReturnType<NewExperimentServices["createPlotsFromGrid"]>>;
      if (plotMapData.type === "generate") {
        onProgress?.(0.3, "Generating plot grid...");
        plotResult = await services.createPlotsFromGrid(
          trialId,
          createdExperiment.id,
          plotMapData.rows,
          plotMapData.columns,
          onPlotProgress,
          scope
        );
      } else {
        onProgress?.(0.3, "Generating sequential plots...");
        plotResult = await services.createPlotsFromSequence(
          trialId,
          createdExperiment.id,
          plotMapData.count,
          plotMapData.customNames,
          onPlotProgress,
          scope
        );
      }

      if (plotResult.success) {
        onProgress?.(1, `Created ${plotResult.createdCount} plots`);
      } else {
        // Rollback: delete the experiment since plot generation failed
        console.error("Plot generation failed:", plotResult.errors);
        onProgress?.(0, "Plot generation failed. Rolling back...");
        try {
          await services.deleteExperiment(trialId, createdExperiment.id, scope);
        } catch (rollbackError) {
          console.error("Failed to rollback experiment after plot generation failure:", rollbackError);
        }
        throw new Error(`Plot generation failed: ${plotResult.errors?.join(", ") || "Unknown error"}`);
      }
    }
  } else {
    // Create experiment without plots
    createdExperiment = await services.createExperimentWithPlots(trialId, experimentData, undefined, undefined, scope);
  }

  return createdExperiment;
}
